import React, { useEffect, useMemo, useState } from "react";
import {
  BlueBirdTheme, Button, Loading, Tooltip, Typography,
} from "@kitbird/core";
import { ColumnI } from "../../../../../components/Table/TableProps";
import { ReactComponent as EditIcon } from "../../../../../assets/icons/edit.svg";
import Table from "../../../../../components/Table";
import Status, { StatusEnums } from "../../../../../components/Status";
import useVendor from "../../useVendor";
import { IGetMasterVendorsData } from "../../../../../services/masterVendorService";
import { StatusFlagColumn } from "../../constants";

interface VendorTableProps {
  setEditValue: React.Dispatch<React.SetStateAction<IGetMasterVendorsData | undefined>>;
  showModal: () => void;
  useCustomHook: ReturnType<typeof useVendor>;
}

const { primary, gray } = BlueBirdTheme.colors;

const VendorTable = ({ setEditValue, showModal, useCustomHook }: VendorTableProps) => {
  const {
    meta,
    obsRef,
    masterVendorList,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    params,
    setParams,
  } = useCustomHook;

  const options = {
    root: obsRef.current,
    rootMargin: "0px",
    threshold: [0],
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({
      isIntersecting,
    }) => {
      if (isIntersecting && !isFetching) {
        fetchNextPage();
      } else if (meta.page === meta.total_pages && !isFetching) {
        fetchNextPage();
      }
    });
  }, options);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(obsRef.current || document.createElement("tfoot"));
    else observer.observe(obsRef.current || document.createElement("tfoot"));
  }, [meta]);

  const renderStatusApproval = (status?: "OPEN" | "CLOSE" | "CANCEL") => {
    const val = status ? {
      OPEN: { text: "Diproses", status: StatusEnums.RATING },
      CLOSE: { text: "Disetujui", status: StatusEnums.SUCCESS },
      CANCEL: { text: "Ditolak", status: StatusEnums.DANGER },
    }[status] : { text: "", status: StatusEnums.INACTIVE };
    return status ? <Status content={val.text} status={val.status} /> : <></>;
  };

  const renderVendorName = (item: any, row: Record<string, any>) => (
    <div className="ellipsis-text">
      <Typography level={6} color={gray.subtitle} fontWeight="medium">{item}</Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>{row.vendor_address}</Typography>
    </div>
  );

  const renderVendorAccount = (item: any, row: Record<string, any>) => (
    <div className="ellipsis-text">
      <Typography level={6} color={gray.subtitle} fontWeight="medium">{row.bank_account} • {row.bank_account_number}</Typography>
      <br />
      <Typography level={7} color={gray.bodyText}>{item}</Typography>
    </div>
  );

  const renderPoolDepartment = (item?: any[]) => (item && item.length
    ? (
      <Tooltip direction="top" tooltipContent={<div>
        {item.map(({ pool_id: poolId, department }) => `${poolId} (${department})`).join(", ")}
      </div>}>
        <div className="ellipsis-text">
          <Typography level={6} color={gray.subtitle} fontWeight="medium">{item[0].pool_id}{item.length > 1 && ` +${item.length - 1}`}</Typography>
          <br />
          <Typography level={7} color={gray.bodyText}>{item[0].department}{item.length > 1 && ` +${item.length - 1}`}</Typography>
        </div>
      </Tooltip>
    )
    : false
  );

  const renderStatusVendor = (status?: "Y" | "N" | "BL") => {
    const val = status ? {
      Y: { text: "Aktif", status: StatusEnums.SUCCESS },
      N: { text: "Non-aktif", status: StatusEnums.INACTIVE },
      BL: { text: "Blacklist", status: StatusEnums.BLACKLIST },
    }[status] : ({ text: "", status: StatusEnums.INACTIVE });
    return status ? <Status content={val.text} status={val.status} /> : <></>;
  };

  const renderActions = (item: any, row: Record<string, any>) => (
    <div style={{ display: "flex", gap: 8 }}>
      <Button
        style={{ padding: "8px 10px" }}
        backgroundColor={
          row.flag_column === StatusFlagColumn.SOME_APPROVED
            ? gray.line
            : primary[300]
        }
        disabled={row.flag_column === StatusFlagColumn.SOME_APPROVED}
      >
        <EditIcon
          width={16}
          height={16}
          color={
            row.flag_column === StatusFlagColumn.SOME_APPROVED
              ? gray.disable
              : "white"
          }
        />
      </Button>
    </div>
  );

  const columns: ColumnI[] = useMemo(() => [
    {
      title: "Status approval",
      dataIndex: "status_approval",
      key: "status_approval",
      width: "200px",
      render: renderStatusApproval,
    },
    {
      title: "Kode vendor",
      dataIndex: "vendor_code",
      key: "vendor_code",
      width: "200px",
      sort: true,
    },
    {
      title: "Tipe vendor",
      dataIndex: "type_vendor",
      key: "type_vendor",
      width: "200px",
    },
    {
      title: "Nama & alamat vendor",
      dataIndex: "vendor_name",
      key: "vendor_name",
      width: "200px",
      render: renderVendorName,
      sort: true,
    },
    {
      title: "Detail rekening vendor",
      dataIndex: "bank_account_name",
      key: "bank_account_name",
      width: "200px",
      render: renderVendorAccount,
      sort: true,
    },
    {
      title: "Status vendor",
      dataIndex: "status_vendor",
      key: "status_vendor",
      width: "200px",
      render: renderStatusVendor,
    },
    {
      title: "Pool & departemen",
      dataIndex: "pool_department",
      key: "pool_department",
      width: "200px",
      render: renderPoolDepartment,
    },
    {
      title: "Action",
      dataIndex: "id",
      key: "id",
      width: "100px",
      sticky: "right",
      render: renderActions,
    },
  ], []);

  return (isFetching && !isFetchingNextPage)
    ? <Loading variant="line" />
    : (
      <Table
        withNumber
        columns={columns}
        dataSource={masterVendorList}
        sortConfig={{
          sortType: params.sortType,
          sortBy: params.sortBy,
          onSort: (name, type) => setParams((curr) => ({ ...curr, sortBy: name, sortType: type })),
        }}
        onClickRow={(row) => {
          setEditValue(row as IGetMasterVendorsData);
          showModal();
        }}
        footer={
          <>
            {(!isFetching && meta.page < meta.total_pages) && <tfoot ref={obsRef} style={{ height: 5, width: "100%" }} />}
            {isFetchingNextPage && <Loading variant="circular" />}
          </>
        }
      />
    );
};

export default VendorTable;
